/** Settings: language, sound, HUD scale, map options. `SettingsContent` is reused by the in-game overlay. */
import { FACTION_COLORS, type UnitType } from '@friction/engine';
import { settingsStore, UNIT_STYLES, type UnitStyle, useSettings } from '../../app/settings';
import { bus } from '../../game/bus';
import { LOCALE_NAME, LOCALES, useT } from '../../i18n';
import { Button, click, Field, Rule, Segmented, Slider, Toggle } from '../common/controls';
import { UnitCounter } from '../common/UnitCounter';
import { ScreenFrame } from './ScreenFrame';
import s from './screens.module.css';
import cs from './settings.module.css';

/** Sample counters for the style previews: our arms in red, two of the enemy's in blue. */
const SAMPLE: [UnitType, number][] = [
  ['infantry', FACTION_COLORS[0]],
  ['motorized', FACTION_COLORS[0]],
  ['armor', FACTION_COLORS[1]],
  ['artillery', FACTION_COLORS[0]],
  ['recon', FACTION_COLORS[1]],
  ['hq', FACTION_COLORS[0]],
];

/** The counter-style chooser: each option shows real counters in its style on a scrap of map. */
function CounterStyles({ value, onChange }: { value: UnitStyle; onChange(v: UnitStyle): void }) {
  const t = useT();
  return (
    <div className={cs.styles}>
      {UNIT_STYLES.map((style) => (
        <button
          key={style}
          type="button"
          aria-pressed={style === value}
          className={`${cs.style} ${style === value ? cs.on : cs.off}`}
          onClick={() => {
            if (style === value) return;
            click();
            onChange(style);
          }}
        >
          <span className={cs.paper} aria-hidden>
            {SAMPLE.map(([type, color]) => (
              <UnitCounter key={type} type={type} color={color} size={46} echelon={false} style={style} />
            ))}
          </span>
          <span className={cs.name}>{t(`settings.counters.${style}`)}</span>
          <span className={cs.desc}>{t(`settings.counters.${style}.desc`)}</span>
        </button>
      ))}
    </div>
  );
}

export function SettingsContent() {
  const t = useT();
  const st = useSettings((x) => x);
  const set = settingsStore.getState().set;
  return (
    <div className={s.stack} style={{ maxWidth: 640, margin: '0 auto', width: '100%' }}>
      <section className={s.section}>
        <Rule>{t('settings.language')}</Rule>
        <Field className={s.card} label={t('settings.language.label')} hint={t('settings.language.hint')}>
          <Segmented
            block
            value={st.language}
            onChange={(v) => set({ language: v })}
            options={LOCALES.map((l) => ({ value: l, label: LOCALE_NAME[l] }))}
          />
        </Field>
      </section>
      <section className={s.section}>
        <Rule>{t('settings.sound')}</Rule>
        <Field
          className={s.card}
          label={t('settings.master', { value: st.muted ? t('settings.muted') : t.pct(st.volume) })}
          hint={t('settings.master.hint')}
        >
          <div className={s.row}>
            <Slider
              value={st.volume}
              min={0}
              max={1}
              step={0.05}
              onChange={(v) => set({ volume: v, muted: false })}
              label={t('settings.master.aria')}
            />
            <Button size="sm" icon="speaker" silent onClick={() => bus.emit('sound', { name: 'stamp' })}>
              {t('ui.test')}
            </Button>
          </div>
        </Field>
        <Field
          className={s.card}
          label={t('settings.ambience', { value: t.pct(st.ambienceVolume) })}
          hint={t('settings.ambience.hint')}
        >
          <Slider
            value={st.ambienceVolume}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => set({ ambienceVolume: v })}
            label={t('settings.ambience.aria')}
          />
        </Field>
        <Field
          className={s.card}
          label={t('settings.effects', { value: t.pct(st.effectsVolume) })}
          hint={t('settings.effects.hint')}
        >
          <div className={s.row}>
            <Slider
              value={st.effectsVolume}
              min={0}
              max={1}
              step={0.05}
              onChange={(v) => set({ effectsVolume: v })}
              label={t('settings.effects.aria')}
            />
            <Button size="sm" icon="radio" silent onClick={() => bus.emit('sound', { name: 'morse' })}>
              {t('ui.test')}
            </Button>
          </div>
        </Field>
        <Field row label={t('settings.mute')} hint={t('settings.mute.hint')}>
          <Toggle on={st.muted} onChange={(v) => set({ muted: v })} label={t('settings.mute')} />
        </Field>
      </section>
      <section className={s.section}>
        <Rule>{t('settings.display')}</Rule>
        <Field
          className={s.card}
          label={t('settings.scale', { value: t.pct(st.uiScale) })}
          hint={t('settings.scale.hint')}
        >
          <Slider
            value={st.uiScale}
            min={0.8}
            max={1.3}
            step={0.05}
            onChange={(v) => set({ uiScale: v })}
            label={t('settings.scale.aria')}
          />
        </Field>
        <Field row label={t('settings.motion')} hint={t('settings.motion.hint')}>
          <Toggle
            on={st.reducedMotion}
            onChange={(v) => set({ reducedMotion: v })}
            label={t('settings.motion')}
          />
        </Field>
        <Field row label={t('settings.newspaper')} hint={t('settings.newspaper.hint')}>
          <Toggle on={st.newspaper} onChange={(v) => set({ newspaper: v })} label={t('settings.newspaper')} />
        </Field>
      </section>
      <section className={s.section}>
        <Rule>{t('settings.mapTable')}</Rule>
        <Field className={s.card} label={t('settings.counters')} hint={t('settings.counters.hint')}>
          <CounterStyles value={st.unitStyle} onChange={(v) => set({ unitStyle: v })} />
        </Field>
        <Field row label={t('settings.edge')} hint={t('settings.edge.hint')}>
          <Toggle on={st.edgeScroll} onChange={(v) => set({ edgeScroll: v })} label={t('settings.edge')} />
        </Field>
        <Field row label={t('settings.grid')} hint={t('settings.grid.hint')}>
          <Toggle on={st.showGrid} onChange={(v) => set({ showGrid: v })} label={t('settings.grid')} />
        </Field>
      </section>
      <div className={s.row}>
        <span className={s.spacer} />
        <Button variant="ghost" onClick={() => settingsStore.getState().reset()}>
          {t('settings.restore')}
        </Button>
      </div>
    </div>
  );
}

export function SettingsScreen() {
  const t = useT();
  return (
    <ScreenFrame kicker={t('settings.kicker')} title={t('settings.title')} width={820} fit>
      <SettingsContent />
    </ScreenFrame>
  );
}
